const REFERENCE_PATH = /^\/news-attachment\/(\d+)\/(\d+)$/

export interface AttachmentReference {
  postId: number
  attachmentId: number
}

// Attribute values are read and written through the DOM, never by string templating, so a value
// cannot break out of its attribute and `&amp;` in a stored link compares equal to `&`.
export function rewriteImageSources(
  html: string,
  rewrite: (src: string) => string | null,
  options?: { noReferrer?: boolean },
): string {
  const template = document.createElement('template')
  template.innerHTML = html
  template.content.querySelectorAll('img').forEach((image) => {
    const src = image.getAttribute('src') ?? ''
    const next = rewrite(src)
    if (next === null) {
      image.remove()
      return
    }
    if (next !== src) {
      image.setAttribute('src', next)
    }
    if (options?.noReferrer) {
      image.setAttribute('referrerpolicy', 'no-referrer')
    }
  })
  return template.innerHTML
}

// An absolute address because the server keeps only absolute http(s) image sources; the path is
// not a route — the client swaps it for a fresh download link whenever the post is shown.
export function buildAttachmentReference(postId: number, attachmentId: number): string {
  return `${window.location.origin}/news-attachment/${postId}/${attachmentId}`
}

// Any origin is accepted, so a body keeps working when copied between environments.
export function parseAttachmentReference(src: string): AttachmentReference | null {
  let url: URL
  try {
    url = new URL(src)
  } catch {
    return null
  }
  if (url.protocol !== 'https:' && url.protocol !== 'http:') {
    return null
  }
  const match = REFERENCE_PATH.exec(url.pathname)
  return match ? { postId: Number(match[1]), attachmentId: Number(match[2]) } : null
}

export function referencedAttachmentIds(html: string, postId: number): number[] {
  const ids = new Set<number>()
  rewriteImageSources(html, (src) => {
    const reference = parseAttachmentReference(src)
    if (reference && reference.postId === postId) {
      ids.add(reference.attachmentId)
    }
    return src
  })
  return Array.from(ids)
}

export function createImageHtml(src: string, alt: string): string {
  const image = document.createElement('img')
  image.setAttribute('src', src)
  image.setAttribute('alt', alt)
  return image.outerHTML
}

// A presigned link's query changes with every issue and may come back re-encoded from the editor,
// so links are matched on origin and path.
export function withoutQuery(src: string): string {
  try {
    const url = new URL(src)
    return `${url.origin}${url.pathname}`
  } catch {
    return src
  }
}
